/**
 * Gives a visible page a rendering opportunity before running non-visual work.
 * rAF alone runs before paint, so the task runs in a timer after rAF instead.
 * The fallback bounds the wait when frames stop; this is not a paint acknowledgement.
 */
export function runAfterPaint(task: () => void): () => void {
    if (typeof window === 'undefined') return () => {};

    let finished = false;
    let frame: number | undefined;
    let afterFrame: number | undefined;
    const cancel = () => {
        finished = true;
        window.clearTimeout(fallback);
        window.clearTimeout(afterFrame);
        if (frame !== undefined) window.cancelAnimationFrame(frame);
    };
    const run = () => {
        if (finished) return;
        cancel();
        task();
    };
    const canRequestFrame = document.visibilityState !== 'hidden' && typeof window.requestAnimationFrame === 'function';
    const fallback = window.setTimeout(run, canRequestFrame ? 100 : 0);
    if (canRequestFrame) {
        frame = window.requestAnimationFrame(() => {
            if (finished) return;
            afterFrame = window.setTimeout(run, 0);
        });
    }
    return cancel;
}
